import {
  Box,
  Stack,
  Typography,
  Chip,
  Pagination,
  Theme,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {useParams} from 'react-router-dom';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {IMediaTrack} from '../../../dto/MediaTrack.dto';
import InputSearch from '../../../components/styles/InputSearch';
import MusicPreviewBtn from '../../../components/music/MusicPreviewBtn';
import {SongSelectionRemoveButton} from '../../../components/Button/SongSelectionRemoveButton';
import {SongSelectionAddButton} from '../../../components/Button/SongSelectionAddButton';
import {GridDurationColumn} from '../../../components/grid/GridColumns';
import {
  IConditionalOperators,
  IDataGridProSearchSort,
  defaultDataGridProSearchSort,
} from '../../../dto/Html.dto';
import {tabsStyleRadioMode} from '../../../components/styles/TabStyles';
import {DataGridCustPagination} from '../../../components/utlis/DataGrid/Pagination';
import {useRqMediaTracksWithTokenPaginate} from '../../../react_query/media_tracks/ListMediaTrackPaginatPro';
import {CheckCircle, Error} from '@mui/icons-material';
import SecondsNumberToTimeFormat from '../../../components/utlis/date-time/SecondsNumberToTimeFormat';
import {makeStyles} from '@mui/styles';
import {useDispatch} from 'react-redux';
import {closePlayer} from '../../../store/Slices/playerSlice';

type Props = {
  selectedTracks: IMediaTrack[];
  onSelect: (track: IMediaTrack) => void;
  onDeselect: (track: IMediaTrack) => void;
  isAddDisabled?: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  iconColor: {
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.contrastText,
    },
  },
}));

export const WalkupMusicSelectionTable = ({
  selectedTracks,
  onSelect,
  onDeselect,
  isAddDisabled,
}: Props) => {
  const dispatch = useDispatch();
  const style = useStyles();
  const {token} = useParams();
  const theme = useTheme();
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedTrackIds, setSelectedTrackIds] = useState<number[]>([]);
  const [queryTab, setQueryTab] = useState<string>('all');

  const [query, setQuery] = React.useState<
    IDataGridProSearchSort &
      (
        | {
            track_length: number;
            track_length_condition: IConditionalOperators;
          }
        | {track_length?: undefined; track_length_condition?: undefined}
      )
  >({
    ...defaultDataGridProSearchSort,
    track_category: 2,
  });
  const {data, isLoading} = useRqMediaTracksWithTokenPaginate(
    token ?? '',
    query,
  );

  const handleSearch = useCallback((val: string) => {
    const trimmedVal = val.trim();
    setQuery(prev => ({
      ...prev,
      search: trimmedVal,
    }));
  }, []);
  const handlePagination = useCallback(
    (paginate: GridPaginationModel) => {
      dispatch(closePlayer()); // Stop any playing song on pagination
      setQuery(prev => ({...prev, ...paginate}));
    },
    [dispatch],
  );
  const handleSortModelChange = (model: GridSortModel) => {
    setQuery(prev => ({...prev, sort: model}));
  };

  const handleSongAdd = useCallback(
    (musicTrack: IMediaTrack) => {
      setSelectedTrackIds(prev => [...prev, musicTrack.id]);
      onSelect(musicTrack);
    },
    [onSelect],
  );

  const handleSongRemove = useCallback(
    (musicTrack: IMediaTrack) => {
      setSelectedTrackIds(prev => prev.filter(id => id !== musicTrack.id));
      onDeselect(musicTrack);
    },
    [onDeselect],
  );

  const handleHitterPithcerToggle = useCallback(
    (
      _event: React.SyntheticEvent | React.MouseEvent,
      newValue: string | null,
    ) => {
      if (!newValue) return;

      setQuery(prev => {
        const updatedQuery = {...prev};
        updatedQuery.track_category = 2;
        if (newValue === 'all') {
          delete updatedQuery.track_length;
          delete updatedQuery.track_length_condition;
        } else {
          updatedQuery.track_length = 45;
          updatedQuery.track_length_condition =
            newValue === 'clip' ? 'clip' : newValue === 'hitter' ? 'lte' : 'gt';
          updatedQuery.track_category = newValue === 'clip' ? 4 : 2; // 4-clip, 2-walkup-library
        }

        return updatedQuery;
      });

      setQueryTab(newValue);
    },
    [setQuery],
  );

  const columns: GridColDef[] = useMemo(
    () =>
      [
        {
          field: 'url',
          type: 'actions',
          headerName: 'Play',
          width: 54,
          sortable: false,
          renderCell: (params: GridRenderCellParams<IMediaTrack>) =>
            params.row.url ? (
              <MusicPreviewBtn
                id={params.row.id}
                url={params.row.url}
                track_name={params.row.track_name}
                track_artist={params.row.track_artist}
              />
            ) : null,
        },
        {
          field: 'track_artist',
          headerName: 'Artist',
          flex: 1,
        },
        {
          field: 'track_name',
          headerName: 'Song Title',
          flex: 1,
        },
        {
          field: 'type',
          headerName: 'Type',
          flex: 1,
          sortable: false,
          renderCell: (params: GridRenderCellParams) =>
            params.row.duration > 45 ? 'Pitcher' : 'Hitter',
        },
        GridDurationColumn,
        {
          type: 'actions',
          field: 'actions',
          headerName: '',
          hideable: false,
          sortable: false,
          width: 125,
          renderCell: (params: GridRenderCellParams<IMediaTrack>) =>
            selectedTrackIds.includes(params.row.id) ? (
              <SongSelectionRemoveButton
                onClick={() => handleSongRemove(params.row)}
              />
            ) : (
              <SongSelectionAddButton
                onClick={() => handleSongAdd(params.row)}
                disabled={isAddDisabled}
              />
            ),
        },
      ].map(column => ({
        ...column,
        filterable: false,
      })),
    [handleSongAdd, handleSongRemove, isAddDisabled, selectedTrackIds],
  );

  useEffect(() => {
    setSelectedTrackIds(selectedTracks.map(o => o.id));
  }, [selectedTracks]);

  const DesktopSongTypeTabs = React.memo(
    ({
      value,
      onChange,
    }: {
      value: string;
      onChange: (event: React.SyntheticEvent, value: string) => void;
    }) => (
      <Tabs
        value={value}
        onChange={onChange}
        variant='scrollable'
        scrollButtons
        allowScrollButtonsMobile
        sx={tabsStyleRadioMode}
        aria-label='tabs'>
        <Tab value='hitter' label='Hitter' />
        <Tab value='all' label='Show All' />
        <Tab value='pitcher' label='Pitcher' />
        <Tab value='clip' label='Custom Clips' />
      </Tabs>
    ),
  );

  const MobileSongTypeTabs = React.memo(
    ({
      value,
      onChange,
    }: {
      value: string;
      onChange: (event: React.SyntheticEvent, value: string) => void;
    }) => (
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={onChange}
        size='small'
        fullWidth
        sx={{
          '& .MuiToggleButton-root': {
            borderRadius: theme.spacing(3),
            fontSize: {xs: '75%', sm: 'auto'},
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': {
              backgroundColor: `${theme.palette.primary.main} !important`,
              color: `${theme.palette.primary.contrastText} !important`,
              fontWeight: 700,
              borderColor: `${theme.palette.primary.main} !important`,
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main} !important`,
              },
            },
          },
        }}>
        <ToggleButton value='all'>All</ToggleButton>
        <ToggleButton value='hitter'>Hitter</ToggleButton>
        <ToggleButton value='pitcher'>Pitcher</ToggleButton>
        <ToggleButton value='clip'>Clips</ToggleButton>
      </ToggleButtonGroup>
    ),
  );

  return (
    <>
      {/* Desktop: Single Row Layout */}
      <Stack
        direction='row'
        alignItems='center'
        spacing={2}
        justifyContent='space-between'
        sx={{display: {xs: 'none', md: 'flex'}}}>
        {/* Tabs */}
        <Box
          sx={{
            maxWidth: theme.breakpoints.values.lg,
            position: 'relative',
          }}>
          <DesktopSongTypeTabs
            value={queryTab}
            onChange={handleHitterPithcerToggle}
          />
        </Box>

        {/* Search and Chip */}
        <Stack direction='row' alignItems='center' spacing={1}>
          <InputSearch
            className='max-w-300'
            placeholder='Search Song'
            value={`${query.search}`}
            onChange={handleSearch}
          />
          <Chip
            label={`${selectedTrackIds.length}`}
            icon={
              !selectedTrackIds.length ? (
                <Error color='warning' />
              ) : (
                <CheckCircle color='success' />
              )
            }
          />
        </Stack>
      </Stack>

      {/* Mobile: Two Row Layout - Fixed at Top */}
      <Stack
        spacing={2}
        sx={{
          display: {xs: 'flex', md: 'none'},
          position: {xs: 'sticky'},
          top: {xs: 0, md: 'auto'},
          zIndex: theme.zIndex.modal,
          pb: 2,
        }}>
        {/* First Row: Horizontal Genre Selector */}
        <Box
          sx={{
            display: {xs: 'block', md: 'none'},
            position: 'relative',
          }}>
          <Box
            className='blue-variant6'
            sx={{
              position: 'sticky',
              top: 0,
            }}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{mb: 1}}>
              <Box
                sx={{
                  width: '100%',
                }}>
                <MobileSongTypeTabs
                  value={queryTab}
                  onChange={handleHitterPithcerToggle}
                />
              </Box>
            </Stack>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{mt: 2}}>
              <InputSearch
                className='max-w-300'
                placeholder='Search Song'
                value={`${query.search}`}
                onChange={handleSearch}
              />
              <Chip
                sx={{ml: theme.spacing(1)}}
                label={`${selectedTrackIds.length}`}
                icon={
                  !selectedTrackIds.length ? (
                    <Error color='warning' />
                  ) : (
                    <CheckCircle color='success' />
                  )
                }
              />
            </Stack>
          </Box>
        </Box>
      </Stack>
      <Box
        sx={{
          display: {xs: 'block', sm: 'none'},
          mt: 1,
          height: 'auto',
          flexDirection: 'column',
        }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}>
            <Box className='h-full w-full'>
              <Stack spacing={1} p={1}>
                {Array.from({length: 5}).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant='rectangular'
                    height={72}
                    sx={{borderRadius: 1}}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        ) : data?.data?.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}>
            <Typography>No tracks found</Typography>
          </Box>
        ) : (
          <>
            <Box
              className='MainBox'
              sx={{flex: 1, overflow: 'auto', mb: 2, height: '100%'}}>
              {data?.data?.map(track => (
                <Stack
                  key={track.id}
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  className='radius-5 MainStack'
                  sx={{border: `1px solid ${theme.palette.divider}`}}
                  m={0.5}
                  p={0.5}>
                  <Stack
                    direction='row'
                    spacing={2}
                    alignItems='center'
                    sx={{flex: 1, minWidth: 0, overflow: 'hidden'}}>
                    {track.url && (
                      <Box className={style.iconColor} sx={{flexShrink: 0}}>
                        <Stack>
                          <MusicPreviewBtn
                            id={track.id}
                            url={track.url}
                            track_name={
                              track.track_name || track.on_demand_label
                            }
                            track_artist={track.track_artist}
                          />
                          <Typography variant='caption'>
                            {SecondsNumberToTimeFormat(track.duration)}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                    <Stack
                      sx={{flex: 1, minWidth: 0, overflow: 'hidden'}}
                      spacing={0.5}>
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'>
                        <Typography
                          title={track.on_demand_label || track.track_name}
                          className={`${
                            track.url ? 'text-white' : 'text-white-50 italic'
                          }`}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '90%',
                            fontSize: '80%',
                          }}>
                          {track.on_demand_label || track.track_name}
                        </Typography>
                      </Stack>
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        className='w-full text-white-50'>
                        <Typography
                          variant='caption'
                          title={track.track_artist}
                          sx={{
                            fontSize: '70%',
                            lineHeight: 1,
                          }}>
                          {track.track_artist}
                        </Typography>
                        {!query?.track_length ? (
                          <Typography
                            variant='caption'
                            title={track.track_artist}
                            sx={{
                              fontSize: '70%',
                              lineHeight: 1,
                            }}>
                            {track.duration > 45 ? 'Pitcher' : 'Hitter'}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Stack>
                  <Box sx={{flexShrink: 0, ml: 1}}>
                    {selectedTrackIds.includes(track.id) ? (
                      <SongSelectionRemoveButton
                        onClick={() => handleSongRemove(track)}
                      />
                    ) : (
                      <SongSelectionAddButton
                        onClick={() => handleSongAdd(track)}
                        disabled={isAddDisabled}
                      />
                    )}
                  </Box>
                </Stack>
              ))}
            </Box>

            {Math.ceil((data?.total ?? 0) / query.pageSize) > 1 && (
              <Box
                sx={{
                  position: {xs: 'fixed', md: 'static'},
                  bottom: {xs: '5%', md: 'auto'},
                  left: {xs: 0, md: 'auto'},
                  right: {xs: 0, md: 'auto'},
                  display: 'flex',
                  justifyContent: 'center',
                  py: 2,
                  borderTop: `1% solid ${theme.palette.divider}`,
                  backgroundColor: {
                    xs: theme.palette.customBgColors.blue_variant6,
                    md: 'transparent',
                  },
                  zIndex: {xs: theme.zIndex.modal, md: 'auto'},
                }}>
                <Pagination
                  count={Math.ceil((data?.total ?? 0) / query.pageSize)}
                  page={query.page + 1}
                  onChange={(_event, page) =>
                    handlePagination({page: page - 1, pageSize: query.pageSize})
                  }
                  color='primary'
                  showFirstButton
                  showLastButton
                  siblingCount={isVerySmallScreen ? 0 : 1}
                  size={isVerySmallScreen ? 'small' : 'medium'}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Desktop View: DataGrid */}
      <Box sx={{display: {xs: 'none', sm: 'block'}, mt: 1, height: 500}}>
        <DataGridPro
          className='dataGrid-header-visible'
          rows={data?.data ?? []}
          columns={columns}
          loading={isLoading}
          rowCount={data?.total ?? 0}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{page: query.page, pageSize: query.pageSize}}
          paginationMode='server'
          pagination={true}
          onPaginationModelChange={handlePagination}
          sortingMode='server'
          onSortModelChange={handleSortModelChange}
          density='compact'
          disableRowSelectionOnClick
          disableColumnMenu
          slots={{
            pagination: DataGridCustPagination,
          }}
          slotProps={{
            columnsPanel: {
              disableHideAllButton: true,
              disableShowAllButton: true,
            },
          }}
        />
      </Box>
    </>
  );
};
